import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import ReviewForm from "@/components/reviews/ReviewForm";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const ReviewEdit = () => {
  const { id: reviewId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isLoading: authLoading } = useAuth();
  const { language } = useLanguage();

  const [review, setReview] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // 調試日誌：組件是否被渲染
  useEffect(() => {
    console.log('🔥 ReviewEdit: Component mounted', { reviewId, authLoading, user: !!user });
  }, []);

  // 調試日誌：追蹤狀態變化
  useEffect(() => {
    console.log('🔥 ReviewEdit: State changed', { 
      reviewId, 
      authLoading, 
      hasUser: !!user,
      loading,
      hasReview: !!review,
      error 
    });
  }, [reviewId, authLoading, user, loading, review, error]);

  // 認證檢查：未登入時重定向到登入頁
  useEffect(() => {
    if (!authLoading && !user) {
      console.log('ReviewEdit: User not authenticated, redirecting to login');
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // 主要的資料獲取邏輯
  useEffect(() => {
    if (!reviewId) {
      console.error('ReviewEdit: No reviewId provided');
      navigate('/reviews');
      return;
    }

    // 等待認證完成
    if (authLoading) {
      return;
    }

    // 確保使用者已登入
    if (!user) {
      return; // 會被上面的 useEffect 處理重定向
    }

    const fetchReview = async () => {
      console.log('ReviewEdit: Fetching review', { reviewId, userId: user.id });
      setLoading(true);
      setError("");

      try {
        // 檢查使用者是否為評論作者
        const { data, error: fetchError } = await supabase
          .from('reviews')
          .select('*')
          .eq('id', reviewId)
          .eq('user_id', user.id) // 關鍵權限檢查：只有作者能編輯
          .maybeSingle();

        if (fetchError) {
          console.error("ReviewEdit: Database error:", fetchError);
          setError(language === 'en' 
            ? "Failed to load review" 
            : "載入評論失敗");
          return;
        }

        if (!data) {
          console.log('ReviewEdit: No review found or permission denied');
          setError(language === 'en' 
            ? "Review not found or you don't have permission to edit it" 
            : "找不到評論或您沒有編輯權限");
          return;
        }

        // 資料轉換和預設值處理
        const transformedReview = {
          ...data,
          // 確保座標格式正確
          coordinates: (() => {
            if (data.coordinates && typeof data.coordinates === 'object' && !Array.isArray(data.coordinates)) {
              const coords = data.coordinates as Record<string, any>;
              if (coords.lat && coords.lng) {
                return { lat: Number(coords.lat), lng: Number(coords.lng) };
              }
            }
            return { lat: 51.0447, lng: -114.0719 };
          })(),
          
          // 確保陣列欄位
          parking_information: Array.isArray(data.parking_information) ? data.parking_information : [],
          utilities_included: data.utilities_included || [],
          image_urls: data.image_urls || [],
          
          // 確保數字欄位
          bedrooms: data.bedrooms || 0,
          living_rooms: data.living_rooms || 0,
          bathrooms: data.bathrooms || 0,
          
          // 確保押金類型正確
          deposit_type: (data.deposit_type === 'amount' || data.deposit_type === 'months') 
            ? data.deposit_type 
            : 'amount',
            
          // 確保布林值
          pet_allowed: data.pet_allowed || false,
        };

        setReview(transformedReview);
        console.log('ReviewEdit: Review loaded successfully');

      } catch (error: any) {
        console.error("ReviewEdit: Unexpected error:", error);
        setError(language === 'en' 
          ? "An unexpected error occurred" 
          : "發生意外錯誤");
      } finally {
        setLoading(false);
      }
    };

    fetchReview();
  }, [reviewId, user, authLoading, language]);

  // 載入超時處理
  useEffect(() => {
    if (!loading) return;

    const timeout = setTimeout(() => {
      console.warn('ReviewEdit: Loading timeout after 10 seconds');
      setLoading(false);
      setError(language === 'en' 
        ? "Loading timeout. Please refresh the page." 
        : "載入超時，請重新整理頁面");
    }, 10000);

    return () => clearTimeout(timeout);
  }, [loading, language]);

  // 錯誤發生時顯示 toast
  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: language === 'en' ? "Error" : "錯誤",
        description: error,
      });
    }
  }, [error, toast, language]);

  const handleGoBack = () => {
    navigate(-1);
  };

  // Loading 狀態
  if (loading) {
    return (
      <MainLayout>
        <div className="container py-6">
          <div className="flex flex-col justify-center items-center h-48 space-y-4">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-muted-foreground">
              {language === 'en' ? 'Loading review...' : '載入評論中...'}
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 錯誤狀態
  if (error || !review) {
    return (
      <MainLayout>
        <div className="container py-6">
          <div className="flex flex-col justify-center items-center h-48 space-y-4">
            <p className="text-muted-foreground text-center">
              {error || (language === 'en' ? 'Review not found' : '找不到評論')}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/member-reviews')}>
                {language === 'en' ? 'Back to My Reviews' : '返回我的評論'}
              </Button>
              <Button variant="outline" onClick={() => window.location.reload()}>
                {language === 'en' ? 'Retry' : '重試'}
              </Button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 成功載入評論，顯示編輯表單
  return (
    <MainLayout>
      <div className="container py-6">
        <Button variant="ghost" onClick={handleGoBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {language === 'en' ? 'Back' : '返回'}
        </Button>
        
        <h1 className="text-2xl font-bold mb-4">
          {language === 'en' ? 'Edit Review' : '編輯評論'}
        </h1>
        
        <ReviewForm initialValues={review} isEditing={true} reviewId={reviewId!} />
      </div>
    </MainLayout>
  );
};

export default ReviewEdit;
